import React from "react";
import { Icon } from "@/components/Icon";

/* Shared admin UI kit — 1:1 with .design-export/07 Admin.dc.html tokens.
   Restrained dark theme: #00D632 green + #EF4444 red on a neutral surface ladder.
   Fonts: Manrope (display/numbers) + Outfit (body). */

const cx = (...a) => a.filter(Boolean).join(" ");

/* ── Panel: rounded card surface (#0C0C0E) ─────────────────────── */
export function Panel({ className, style, children, ...p }) {
  return (
    <div className={cx("rounded-[18px] border border-[#1F1F23] bg-[#0C0C0E]", className)} style={style} {...p}>
      {children}
    </div>
  );
}

/* ── PageHeader: big title + meta + right-aligned actions ───────── */
/* `as` lets a page render the title as a real heading (default <h1>) so the
   admin surfaces expose a proper document outline to assistive tech; visual
   styling is unchanged. */
export function PageHeader({ title, meta, actions, as: Title = "h1" }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Title className="m-0 font-display text-[28px] font-extrabold tracking-[-.03em]">{title}</Title>
      {meta && <span className="text-[13px] font-medium text-[#71717a]">{meta}</span>}
      {actions && <div className="ml-auto flex gap-2">{actions}</div>}
    </div>
  );
}

/* ── Buttons ────────────────────────────────────────────────────── */
export function BtnPrimary({ className, children, ...p }) {
  return (
    <button
      className={cx("flex h-[38px] items-center gap-1.5 rounded-[10px] bg-[#00D632] px-3.5 font-display text-[13px] font-extrabold text-[#0A0A0A] hover:bg-[#00c02d] disabled:opacity-50", className)}
      {...p}
    >{children}</button>
  );
}
export function BtnGhost({ className, children, ...p }) {
  return (
    <button
      className={cx("flex h-[38px] items-center gap-1.5 rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3.5 text-[13px] font-semibold text-white hover:bg-[#1a1a1e] disabled:opacity-50", className)}
      {...p}
    >{children}</button>
  );
}
export function BtnDanger({ className, children, ...p }) {
  return (
    <button
      className={cx("flex items-center justify-center rounded-lg px-2.5 py-[7px] text-[12px] font-bold text-[#EF4444] hover:bg-[rgba(239,68,68,.08)]", className)}
      style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,.45)" }}
      {...p}
    >{children}</button>
  );
}
/* small inset-ring action button used inside table rows */
export function RowBtn({ className, tone, children, ...p }) {
  const green = tone === "green";
  return (
    <button
      className={cx("rounded-lg px-[9px] py-1.5 text-[11px] font-semibold", green ? "text-[#00D632]" : "text-white", className)}
      style={{ background: green ? "rgba(0,214,50,.12)" : "#18181C", boxShadow: green ? "inset 0 0 0 1px rgba(0,214,50,.4)" : "inset 0 0 0 1px #26262B" }}
      {...p}
    >{children}</button>
  );
}

/* ── StatTile + grid ────────────────────────────────────────────── */
export function StatTile({ icon, label, value, valueColor = "#fff", iconColor = "#a1a1aa" }) {
  return (
    <Panel className="flex flex-col gap-1.5 bg-[#111114] p-3.5">
      <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[.12em] text-[#71717a]">
        {icon && <Icon name={icon} size={14} style={{ color: iconColor }} />}
        {label}
      </div>
      <span className="font-display text-[26px] font-extrabold leading-none" style={{ color: valueColor }}>{value}</span>
    </Panel>
  );
}
/* Responsive stat grid — stacks to 2 cols on phones, fans out to `cols` at lg.
   Literal class strings so Tailwind JIT picks them up. */
const STATGRID_COLS = {
  2: "grid-cols-2",
  3: "grid-cols-2 sm:grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-4",
  5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
  6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
};
export function StatGrid({ children, cols = 5 }) {
  return <div className={cx("grid gap-2.5", STATGRID_COLS[cols] || "grid-cols-2 sm:grid-cols-3")}>{children}</div>;
}

/* ── FilterChips: pill row (+ optional search) ─────────────────── */
export function Chip({ active, children, ...p }) {
  return (
    <button
      className={cx("rounded-[9px] px-3 py-[7px] text-[12px]", active ? "bg-white font-bold text-[#0A0A0A]" : "bg-[#141417] font-semibold text-[#d4d4d8] hover:text-white")}
      {...p}
    >{children}</button>
  );
}
export function SearchBox({ placeholder = "Search…", className, ...p }) {
  return (
    <input
      className={cx("h-[34px] rounded-[9px] border border-[#1F1F23] bg-[#141417] px-2.5 text-[12px] font-medium text-white placeholder:text-[#52525b] focus:outline-none focus:ring-1 focus:ring-[#00D632]/40", className)}
      placeholder={placeholder}
      {...p}
    />
  );
}

/* ── StatusPill ─────────────────────────────────────────────────── */
/* Each state carries a tiny leading glyph so the state is legible without
   relying on color alone (color-blind / low-vision affordance). The visible
   text label already differentiates states; the glyph reinforces tone. */
const STATUS_MAP = {
  LIVE: { bg: "rgba(239,68,68,.14)", fg: "#EF4444", glyph: "●" },
  OPEN: { bg: "rgba(0,214,50,.12)", fg: "#00D632", glyph: "▸" },
  PAUSED: { bg: "#26262B", fg: "#d4d4d8", glyph: "⏸" },
  SETTLED: { bg: "#18181C", fg: "#a1a1aa", glyph: "✓" },
};
export function StatusPill({ status, bg, fg, glyph }) {
  const m = STATUS_MAP[String(status).toUpperCase()] || {};
  const g = glyph ?? m.glyph;
  return (
    <span
      className="inline-flex items-center gap-1 justify-self-start rounded-md px-2 py-[3px] text-[10px] font-extrabold tracking-[.08em]"
      style={{ background: bg || m.bg || "#26262B", color: fg || m.fg || "#d4d4d8" }}
    >
      {g && <span aria-hidden="true" className="text-[9px] leading-none">{g}</span>}
      {status}
    </span>
  );
}

/* ── DataTable: grid table with header + rows ───────────────────── */
export function DataTable({ cols, header, children, className }) {
  const gtc = cols;
  /* Horizontal-scroll on narrow screens: the fixed grid keeps its column widths and
     scrolls inside the card instead of squishing/overlapping or getting clipped. The
     header + rows share one min-w-max track so columns stay aligned while scrolling. */
  return (
    <div className={cx("overflow-x-auto rounded-[14px] border border-[#1F1F23] bg-[#111114]", className)}>
      <div className="min-w-max lg:min-w-0" role="table">
        <div role="row" className="grid gap-2.5 bg-[#141417] px-3.5 py-2.5 text-[10px] font-semibold tracking-[.14em] text-[#71717a]" style={{ gridTemplateColumns: gtc }}>
          {header.map((h, i) => (
            <span key={i} role="columnheader" style={{ textAlign: h.right ? "right" : "left" }}>{h.label ?? h}</span>
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}
export function TRow({ cols, children, className, ...p }) {
  /* Tag each direct cell element with role="cell" so the grid exposes a valid
     row→cell structure to assistive tech (cells are caller-supplied spans/divs). */
  const cells = React.Children.map(children, (c) =>
    React.isValidElement(c) && !c.props.role
      ? React.cloneElement(c, { role: "cell" })
      : c
  );
  return (
    <div role="row" className={cx("grid items-center gap-2.5 border-t border-[#1A1A1E] px-3.5 py-2.5 text-[13px] font-medium", className)} style={{ gridTemplateColumns: cols }} {...p}>
      {cells}
    </div>
  );
}

/* ── SectionCard header (panel title + count pill + right slot) ─── */
export function CardHead({ title, count, right }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="font-display text-[18px] font-extrabold">{title}</span>
      {count != null && (
        <span className="rounded-full bg-white/[.06] px-2 py-[3px] text-[11px] font-bold text-[#E4E4E7]">{count}</span>
      )}
      {right && <div className="ml-auto">{right}</div>}
    </div>
  );
}

/* ── Loading / empty states ─────────────────────────────────────── */
export function EmptyRow({ children = "Nothing here yet." }) {
  return <div className="border-t border-[#1A1A1E] px-3.5 py-8 text-center text-[13px] text-[#52525b]">{children}</div>;
}
export function Loading({ children = "Loading…" }) {
  return <div className="px-3.5 py-8 text-center text-[13px] text-[#52525b]">{children}</div>;
}

export { cx };
